import { Box, Card, Typography } from "@mui/material";
import { useState, useCallback, useMemo } from "react";
import { OverlayVsStockEditModal } from "./../Charts/OverlayVsStockEditModal";
import { overlayVsStockChartStyles } from "../../../../styles/OverlayVsStockChart.styles";
import {
  getBarDirection,
  getBarOffset,
  getBarWidth,
  getBarWidthPercentage,
} from "./comparisonChartUtils";
import { asPercentage, toFiniteNumber } from "components/ui/formatters";
import { UNAVAILABLE_DISPLAY } from "../reportFieldStates";

const DEFAULT_CHART_LIMIT = 100;

// Every one of these arrives from a localStorage-backed report value, so the
// union is what it has always really been -- declaring `number` only meant the
// four call sites cast the truth away with `as number`.
interface OverlayVsStockChartProps {
  stockOnlyValue: number | string;
  stockOverlayValue: number | string;
  limit?: number | string;
  negativeLimit?: number | string;
  onLimitChange?: (limit: number) => void;
  onNegativeLimitChange?: (negativeLimit: number) => void;
}

// A bar's label. The chart used to keep a private copy of the percentage
// formatter, and that copy was the second `value.toFixed` in this codebase to
// take the whole page down when a refused figure reached it. It now shares the
// guarded one, and says N/A for a figure there is none of -- the same answer
// the cards give, rather than a 0.00% that would claim a real result.
const asBarLabel = (value: number | null) =>
  value === null ? UNAVAILABLE_DISPLAY : asPercentage(value);

export const OverlayVsStockChart = ({
  stockOnlyValue,
  stockOverlayValue,
  limit: controlledLimit,
  negativeLimit: controlledNegativeLimit,
  onLimitChange,
  onNegativeLimitChange,
}: OverlayVsStockChartProps) => {
  const [internalLimit, setInternalLimit] = useState(DEFAULT_CHART_LIMIT);
  const [internalNegativeLimit, setInternalNegativeLimit] =
    useState(-DEFAULT_CHART_LIMIT);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Resolved once, because the bar geometry and the bar label both need a real
  // number: an unresolved value reached `toFixed` and crashed the page, and
  // reached the width maths and rendered a `NaN%` CSS width. `??` alone is not
  // enough -- it only catches null and undefined, and the sentinel here is "".
  const stockOnly = toFiniteNumber(stockOnlyValue);
  const stockOverlay = toFiniteNumber(stockOverlayValue);
  const limit =
    toFiniteNumber(controlledLimit ?? internalLimit) ?? DEFAULT_CHART_LIMIT;
  const negativeLimit =
    toFiniteNumber(controlledNegativeLimit ?? internalNegativeLimit) ??
    -DEFAULT_CHART_LIMIT;

  // A bar has to have a width, so a figure that is absent draws nothing rather
  // than drawing NaN. The LABEL still says N/A -- the zero is geometry, never a
  // claim about the figure.
  const stockOnlyMagnitude = stockOnly ?? 0;
  const stockOverlayMagnitude = stockOverlay ?? 0;

  const handleClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      if (onLimitChange) {
        onLimitChange(newLimit);
      } else {
        setInternalLimit(newLimit);
      }
    },
    [onLimitChange],
  );

  const handleNegativeLimitChange = useCallback(
    (newNegativeLimit: number) => {
      if (onNegativeLimitChange) {
        onNegativeLimitChange(newNegativeLimit);
      } else {
        setInternalNegativeLimit(newNegativeLimit);
      }
    },
    [onNegativeLimitChange],
  );

  const negativeLimitMagnitude = Math.abs(negativeLimit);

  const stockOnlyPercentage = useMemo(
    () => getBarWidthPercentage(stockOnlyMagnitude, limit, negativeLimitMagnitude),
    [stockOnlyMagnitude, limit, negativeLimitMagnitude],
  );

  const stockOverlayPercentage = useMemo(
    () =>
      getBarWidthPercentage(stockOverlayMagnitude, limit, negativeLimitMagnitude),
    [stockOverlayMagnitude, limit, negativeLimitMagnitude],
  );

  const hasMixedSigns =
    (stockOnlyMagnitude < 0 && stockOverlayMagnitude > 0) ||
    (stockOnlyMagnitude > 0 && stockOverlayMagnitude < 0);

  const stockOnlyFilledWidth = useMemo(
    () => getBarWidth(stockOnlyPercentage, hasMixedSigns),
    [stockOnlyPercentage, hasMixedSigns],
  );

  const stockOverlayFilledWidth = useMemo(
    () => getBarWidth(stockOverlayPercentage, hasMixedSigns),
    [stockOverlayPercentage, hasMixedSigns],
  );

  const stockOnlyBarDirection = getBarDirection(stockOnlyMagnitude);
  const stockOverlayBarDirection = getBarDirection(stockOverlayMagnitude);

  const rows = [
    {
      key: "stock-only",
      label: "Stock Only",
      value: stockOnly,
      width: stockOnlyFilledWidth,
      direction: stockOnlyBarDirection,
      filledStyle: overlayVsStockChartStyles.barStockOnlyFilled,
      rowSx: overlayVsStockChartStyles.barRow,
    },
    {
      key: "stock-overlay",
      label: "Stock + Overlay",
      value: stockOverlay,
      width: stockOverlayFilledWidth,
      direction: stockOverlayBarDirection,
      filledStyle: overlayVsStockChartStyles.barStockOverlayFilled,
      rowSx: {
        ...overlayVsStockChartStyles.barRow,
        marginBottom: 0,
      },
    },
  ] as const;

  return (
    <>
      <Card
        sx={overlayVsStockChartStyles.card}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-label="Click to edit chart limits"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <Typography variant="h6" sx={overlayVsStockChartStyles.title}>
          Overlay vs Stock Only{" "}
          <Typography
            component="span"
            sx={overlayVsStockChartStyles.titleIllustrative}
          >
            (Illustrative)
          </Typography>
        </Typography>

        <Box sx={overlayVsStockChartStyles.barsContainer}>
          {rows.map((row) => (
            <Box key={row.key} sx={row.rowSx}>
              <Typography sx={overlayVsStockChartStyles.barLabel}>
                {row.label}
              </Typography>
              <Box sx={overlayVsStockChartStyles.barContainer}>
                <Box
                  sx={{
                    ...row.filledStyle,
                    width: row.width,
                    left:
                      row.direction === "left"
                        ? getBarOffset(hasMixedSigns)
                        : "auto",
                    right:
                      row.direction === "right"
                        ? getBarOffset(hasMixedSigns)
                        : "auto",
                  }}
                />
              </Box>
              <Typography sx={overlayVsStockChartStyles.barValue}>
                {asBarLabel(row.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>

      <OverlayVsStockEditModal
        isOpened={isModalOpen}
        onClose={handleCloseModal}
        limit={limit}
        negativeLimit={negativeLimit}
        onLimitChange={handleLimitChange}
        onNegativeLimitChange={handleNegativeLimitChange}
      />
    </>
  );
};
